import React, { useState, useContext, useLayoutEffect } from 'react';

import * as S from './styles';
import DynamicPageContext from 'context/DynamicPage';
import LoadGeneralContext from 'context/LoadGeneralContext';
import SystemNotifyContext from 'context/SystemNotifyContext';
import Check from 'components/Buttons/Check';
import { verifyIfFormCanSave } from './functions';
import SimpleInput from 'components/SimpleInput';
import { formatDateToISO, formatToDateCustom } from 'utils/functions';
import useDebounce from 'hooks/useDebounce';
import Load from 'components/Load';
import { getProductBySKU } from './reqs';
import { Product } from 'types/Product';
import { png } from 'utils/imgList';

export interface DataFormProps {
	countdown: boolean;
	description: string;
	name: string;
	reference_id: string;
	product: Product | null;
	schedule_start: string;
	schedule_end: string;
}

const initialState: DataFormProps = {
	countdown: false,
	description: '',
	name: '',
	product: null,
	schedule_start: '',
	schedule_end: '',
	reference_id: ''
};

export default function InputsSingleProduct() {
	const { setMessage, setIsOpenLoad } = useContext(LoadGeneralContext);
	const { handleNotify } = useContext(SystemNotifyContext);
	const {
		moduleInSavingMode,
		setModuleInSavingMode,
		setCanSaveModuleOnBack,
		setMessageErrorForm,
		moduleOnPhoneToEdit,
		setModuleOnPhoneToEdit
	} = useContext(DynamicPageContext);

	const [dataForm, setDataForm] = useState<DataFormProps>(initialState);
	const [isShowDateInputs, setIsShowDateInputs] = useState(false);
	const [isSearchProduct, setIsSearchProduct] = useState(false);
	const [editMode, setEditMode] = useState(false);
	const [listProducts, setListProducts] = useState<Product[]>([]);

	const referenceIdToSearch = useDebounce(dataForm.reference_id, 1000);

	//busca o produto pelo sku com debounce
	useLayoutEffect(() => {
		(async () => {
			if (
				!editMode &&
				typeof referenceIdToSearch === 'string' &&
				referenceIdToSearch !== ''
			) {
				setDataForm((obj) => ({ ...obj, product: null }));
				setIsSearchProduct(true);
				setIsOpenLoad(true);
				setMessage('Buscando produto');

				const { isSuccess, products } = await getProductBySKU(
					referenceIdToSearch,
					handleNotify
				);

				if (isSuccess && products.length > 0) {
					setListProducts(products);
				}

				setIsSearchProduct(false);
				setIsOpenLoad(false);
			}

			if (referenceIdToSearch === '') {
				setListProducts([]);
				setDataForm((obj) => ({ ...obj, product: null }));
			}
		})();
	}, [referenceIdToSearch]);

	//reseta os dados de agendamento os inputs não estejam visiaveis
	useLayoutEffect(() => {
		if (!isShowDateInputs) {
			setDataForm((obj) => ({
				...obj,
				countdown: false,
				schedule_end: '',
				schedule_start: ''
			}));
		}
	}, [isShowDateInputs]);

	useLayoutEffect(() => {
		//se esta criando um novo componente
		if (moduleInSavingMode !== null) {
			setModuleInSavingMode((obj) => {
				if (obj) {
					return { ...obj, data: dataForm };
				}
				return obj;
			});
		} else {
			//se esta editando um componente
			setModuleOnPhoneToEdit((obj) => {
				if (obj) {
					return { ...obj, data: dataForm };
				}
				return obj;
			});
		}

		verifyIfFormCanSave(dataForm, setCanSaveModuleOnBack, setMessageErrorForm);
	}, [dataForm]);

	//se estiver em edição aqui pega os dados do item editado
	useLayoutEffect(() => {
		if (moduleOnPhoneToEdit !== null && moduleInSavingMode === null) {
			setDataForm((obj) => ({ ...obj, ...moduleOnPhoneToEdit.data }));

			if (
				moduleOnPhoneToEdit?.data?.schedule_start !== '' ||
				moduleOnPhoneToEdit?.data?.schedule_end !== ''
			) {
				setIsShowDateInputs(true);
			}

			if (moduleOnPhoneToEdit?.data?.product) {
				setEditMode(true);
			}
		}
	}, []);

	return (
		<S.container>
			<S.twoInputs>
				<SimpleInput
					label="Título *"
					initialValue={dataForm.name}
					getValue={(value: string) => {
						setDataForm((obj) => ({ ...obj, name: value }));
					}}
				/>

				<SimpleInput
					label="Descrição"
					initialValue={dataForm.description}
					getValue={(value: string) => {
						setDataForm((obj) => ({ ...obj, description: value }));
					}}
				/>
			</S.twoInputs>

			<S.inputAndProductFound>
				<SimpleInput
					label="SKU do produto *"
					initialValue={dataForm.reference_id}
					getValue={(value: string) => {
						setEditMode(false);
						setDataForm((obj) => ({ ...obj, reference_id: value }));
					}}
				/>

				{!isSearchProduct &&
					listProducts.length === 0 &&
					(editMode ? (
						<S.product
							$isSelected={false}
							onClick={() => {
								setDataForm((obj) => ({
									...obj,
									reference_id: '',
									product: null
								}));
								setEditMode(false);
							}}
						>
							<img
								src={
									dataForm?.product?.items?.[0]?.images?.[0]?.imageUrl
										? dataForm?.product?.items?.[0]?.images?.[0]?.imageUrl
										: png.genericImage
								}
								alt={dataForm.name}
							/>
							<p>{dataForm?.product?.productName}</p>
						</S.product>
					) : (
						<S.emptyList>
							<p>Lista de produtos vázia</p>
						</S.emptyList>
					))}

				{isSearchProduct ? (
					<S.centralize>
						<Load showText text="Buscando produto" />
					</S.centralize>
				) : (
					listProducts.length > 0 && (
						<S.listProducts>
							{listProducts.map((product) => (
								<S.product
									key={product.productId}
									$isSelected={
										dataForm?.product?.productId === product.productId
									}
									onClick={() => {
										if (dataForm?.product?.productId === product.productId) {
											setDataForm((obj) => ({ ...obj, product: null }));
										} else {
											setDataForm((obj) => ({ ...obj, product }));
										}
									}}
								>
									<img
										src={
											product.items[0]?.images[0]?.imageUrl || png.genericImage
										}
										alt={product.productName}
									/>
									<p>{product.productName}</p>
								</S.product>
							))}
						</S.listProducts>
					)
				)}
			</S.inputAndProductFound>

			<div style={{ display: 'none' }}>
				<Check
					text="Programação"
					isChecked={isShowDateInputs}
					setIsChecked={() => {
						setIsShowDateInputs((obj) => !obj);
					}}
				/>

				{isShowDateInputs && (
					<>
						<S.inputsDate>
							<SimpleInput
								typeInput="datetime-local"
								label="Ínicio"
								initialValue={
									dataForm.schedule_start === ''
										? ''
										: formatToDateCustom(dataForm.schedule_start)
								}
								getValue={(value: string) => {
									setDataForm((obj) => ({
										...obj,
										schedule_start: formatDateToISO(value)
									}));
								}}
								paddingInLineEnd
							/>

							<SimpleInput
								typeInput="datetime-local"
								label="Fim"
								initialValue={
									dataForm.schedule_end === ''
										? ''
										: formatToDateCustom(dataForm.schedule_end)
								}
								getValue={(value: string) => {
									setDataForm((obj) => ({
										...obj,
										schedule_end: formatDateToISO(value)
									}));
								}}
								paddingInLineEnd
							/>
						</S.inputsDate>

						<Check
							text="Contagem regressiva"
							isChecked={dataForm.countdown}
							setIsChecked={() => {
								setDataForm((obj) => ({
									...obj,
									countdown: !obj.countdown
								}));
							}}
						/>
					</>
				)}
			</div>
		</S.container>
	);
}
